/**
 * Debug overlays (architecture §8.3 `overlay=combat|perf|ui|anim`; GDD alias `fps=1` = perf). This module draws the
 * perf HUD; the other ids are published on `<html data-overlay="…">` for their owners (combat → P4, ui → F3,
 * anim → creatures) to read. F2 toggles the perf HUD in debug builds.
 */
export interface PerfSample {
  fps: number; cpuMsAvg: number; cpuMsP95: number; slowFrames: number;
  presentMs: number; simMs: number; paused: boolean; timeScale: number; cadenceHz: number | null;
  screen: string | null; lite: boolean; errors: number;
  draws?: number; tris?: number; gpuMs?: number | null;
}

const STYLE = `
.mc-perf { position: fixed; z-index: 100; top: calc(env(safe-area-inset-top, 0px) + 6px); right: 6px; pointer-events: none;
  min-width: 150px; padding: 6px 8px; border-radius: 4px; background: rgba(15,28,77,.86); color: #F7F1E1;
  border: 1px solid #D4A537; font: 11px/1.35 ui-monospace, Consolas, monospace; white-space: pre; }
.mc-perf b { color: #F3D77A; font-weight: 700; }
.mc-perf .bad { color: #ff8f7a; }
`;

export class PerfOverlay {
  private el: HTMLElement | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly sample: () => PerfSample) {}

  get visible(): boolean { return this.el !== null; }

  toggle(): void { if (this.el) this.hide(); else this.show(); }

  show(): void {
    if (this.el) return;
    if (!document.getElementById('mc-perf-style')) {
      const s = document.createElement('style');
      s.id = 'mc-perf-style';
      s.textContent = STYLE;
      document.head.appendChild(s);
    }
    const el = document.createElement('div');
    el.className = 'mc-perf';
    el.setAttribute('aria-hidden', 'true');
    (document.getElementById('overlay') ?? document.body).appendChild(el);
    this.el = el;
    const draw = (): void => {
      const s = this.sample();
      const f = (n: number, d = 1): string => n.toFixed(d);
      el.innerHTML = [
        `<b>${f(s.fps, 0)}</b> fps  cpu ${f(s.cpuMsAvg, 2)}/${f(s.cpuMsP95, 2)} ms`,
        `slow ${s.slowFrames}${s.cadenceHz ? `  cad ${s.cadenceHz} Hz` : ''}${s.timeScale !== 1 ? `  ×${s.timeScale}` : ''}`,
        `t ${f(s.presentMs, 1)} / sim ${f(s.simMs, 1)}${s.paused ? '  <b>paused</b>' : ''}`,
        s.draws !== undefined ? `draws ${s.draws}  tris ${s.tris ?? 0}${s.gpuMs != null ? `  gpu ${f(s.gpuMs, 2)}` : ''}` : (s.lite ? 'lite «Без миниатюр»' : 'renderer —'),
        `${s.screen ?? '—'}${s.errors ? `  <span class="bad">err ${s.errors}</span>` : ''}`,
      ].join('\n');
    };
    draw();
    this.timer = setInterval(draw, 250);
  }

  hide(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.el?.remove();
    this.el = null;
  }
}
